import type { EnhancedConversionData } from '@/lib/enhanced-conversions'

export const AD_PARAMS_KEY = 'craft_ad_params'
export const LEAD_FLAG_KEY = 'craft_lead_submitted'
export const LEAD_USER_DATA_KEY = 'craft_lead_user_data'
const LEAD_USER_DATA_TTL_MS = 5 * 60 * 1000
export const LANDING_META_KEY = 'craft_landing_meta'

/**
 * Ordered by how useful each key is to a human reading the lead email. The
 * order matters: `formatAdTrackingForMessage()` renders in this sequence and
 * drops trailing lines if the block runs long, so the identifying fields must
 * come before the long opaque click IDs.
 *
 * The `gad_*` keys arrive from the Google Ads final URL suffix (ValueTrack).
 * See `docs/AD_TRACKING.md`.
 */
const TRACKED_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gad_campaignid',
  'gad_adgroupid',
  'gad_creative',
  'gad_network',
  'gad_device',
  'gad_source',
  'gclid',
  'gbraid',
  'wbraid',
]

/**
 * Google Ads ValueTrack `{network}` codes. A paid lead whose network is `ytv`
 * came from a YouTube video ad, not from someone searching Google — the single
 * most useful thing for whoever calls the lead back.
 */
const NETWORK_LABELS: Record<string, string> = {
  g: 'Google Search',
  s: 'Search partner',
  d: 'Display Network',
  ytv: 'YouTube video',
  ytc: 'YouTube channel',
  vp: 'Video partners',
  gtv: 'Google TV',
}

/** Google Ads ValueTrack `{device}` codes. */
const DEVICE_LABELS: Record<string, string> = {
  m: 'Mobile',
  t: 'Tablet',
  c: 'Computer',
}

/**
 * Cap for the tracking block appended to the lead message. Comfortably above a
 * fully-populated real block (~500 chars incl. a ~100-char gclid) so the
 * line-aware trim in `formatAdTrackingForMessage()` is a backstop, not routine.
 */
const MAX_TRACKING_CHARS = 800

export function captureAdParams(search?: string): void {
  if (typeof window === 'undefined') return

  try {
    const query = search ?? window.location.search
    const params = new URLSearchParams(query)
    const captured: Record<string, string> = {}

    for (const key of TRACKED_KEYS) {
      const value = params.get(key)
      if (value) captured[key] = value
    }

    if (Object.keys(captured).length === 0) return

    const payload = {
      params: captured,
      landedAt: new Date().toISOString(),
      landingPage: window.location.pathname,
    }

    window.sessionStorage.setItem(AD_PARAMS_KEY, JSON.stringify(payload))
  } catch {
    // Safari private mode and other restricted environments may throw.
  }
}

export function getStoredAdParams(): {
  params: Record<string, string>
  landedAt: string
  landingPage: string
} | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(AD_PARAMS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.params === 'object' &&
      typeof parsed.landedAt === 'string' &&
      typeof parsed.landingPage === 'string'
    ) {
      return parsed as {
        params: Record<string, string>
        landedAt: string
        landingPage: string
      }
    }
  } catch {
    // Ignore parse/read errors.
  }

  return null
}

export function formatAdTrackingForMessage(): string {
  if (typeof window === 'undefined') return ''

  const stored = getStoredAdParams()
  if (!stored) return ''

  const lines: string[] = []

  // Decoded first: whoever reads the lead email needs "YouTube video" and the
  // campaign, not a bare ValueTrack code buried under the utm_* pairs.
  const network = stored.params.gad_network
  const device = stored.params.gad_device
  if (network) lines.push(`Network: ${NETWORK_LABELS[network] ?? network}`)
  if (device) lines.push(`Device: ${DEVICE_LABELS[device] ?? device}`)

  for (const [key, value] of Object.entries(stored.params)) {
    // Already rendered above in decoded form.
    if (key === 'gad_network' || key === 'gad_device') continue
    if (value) lines.push(`${key}: ${value}`)
  }

  if (stored.landingPage) {
    lines.push(`landing: ${stored.landingPage}`)
  }

  if (lines.length === 0) return ''

  const header = '\n\n— Ad tracking —\n'

  // Trim whole lines, never mid-value. Slicing the raw string could truncate a
  // gclid into something that looks valid but resolves to nothing, which would
  // silently break tracing a lead back to its click.
  const kept: string[] = []
  let length = header.length
  for (const line of lines) {
    const cost = kept.length === 0 ? line.length : line.length + 1
    if (length + cost > MAX_TRACKING_CHARS) break
    kept.push(line)
    length += cost
  }

  if (kept.length === 0) return ''

  return header + kept.join('\n')
}

/**
 * First-touch entry-page metadata, captured once per session independent of
 * whether the visitor arrived with any ad params. Backs the `landingPage` /
 * `referrer` structured fields on the lead-intake payload — unlike
 * `captureAdParams()` (last-click-wins for paid params), this must NOT be
 * overwritten by later in-app navigation, or a lead who browsed five pages
 * before converting would get credited to the wrong "referrer".
 */
export function captureLandingMeta(pathname?: string): void {
  if (typeof window === 'undefined') return

  try {
    if (window.sessionStorage.getItem(LANDING_META_KEY)) return

    const payload = {
      landingPage: pathname ?? window.location.pathname,
      referrer: document.referrer || '',
      landedAt: new Date().toISOString(),
    }

    window.sessionStorage.setItem(LANDING_META_KEY, JSON.stringify(payload))
  } catch {
    // Safari private mode and other restricted environments may throw.
  }
}

export function getLandingMeta(): {
  landingPage: string
  referrer: string
  landedAt: string
} | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(LANDING_META_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.landingPage === 'string' &&
      typeof parsed.referrer === 'string' &&
      typeof parsed.landedAt === 'string'
    ) {
      return parsed as { landingPage: string; referrer: string; landedAt: string }
    }
  } catch {
    // Ignore parse/read errors.
  }

  return null
}

/**
 * Structured attribution fields for the lead-intake payload — the
 * machine-readable counterpart to `formatAdTrackingForMessage()`'s
 * human-readable block. Keys are omitted (not empty-stringed) when unknown,
 * so callers can spread the result straight into a JSON body without
 * shipping a wall of blank fields.
 */
export function getCaptureAttributionFields(): {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  gclid?: string
  landingPage?: string
  referrer?: string
} {
  const ad = getStoredAdParams()
  const landing = getLandingMeta()

  const fields: {
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    gclid?: string
    landingPage?: string
    referrer?: string
  } = {}

  if (ad?.params.utm_source) fields.utmSource = ad.params.utm_source
  if (ad?.params.utm_medium) fields.utmMedium = ad.params.utm_medium
  if (ad?.params.utm_campaign) fields.utmCampaign = ad.params.utm_campaign
  if (ad?.params.gclid) fields.gclid = ad.params.gclid

  // First-touch landing meta wins when present; a stored ad-params snapshot
  // (which only exists when a tracked param was present) is the fallback for
  // sessions that started before this capture existed.
  const landingPage = landing?.landingPage || ad?.landingPage
  if (landingPage) fields.landingPage = landingPage

  if (landing?.referrer) fields.referrer = landing.referrer

  return fields
}

export function markLeadSubmitted(source?: string, enhancedData?: EnhancedConversionData): void {
  if (typeof window === 'undefined') return

  try {
    // The flag value doubles as the lead's originating form, so /thank-you can
    // attribute the GA4 lead event without threading state through the URL.
    // Callers with no source (and legacy sessions) store '1'; any non-empty
    // value means "a lead was submitted".
    window.sessionStorage.removeItem(LEAD_USER_DATA_KEY)
    window.sessionStorage.setItem(LEAD_FLAG_KEY, source || '1')
    if (enhancedData) {
      window.sessionStorage.setItem(
        LEAD_USER_DATA_KEY,
        JSON.stringify({
          data: enhancedData,
          expiresAt: Date.now() + LEAD_USER_DATA_TTL_MS,
        }),
      )
    }
  } catch {
    // Safari private mode may throw.
  }
}

export function peekLeadSubmitted(): boolean {
  if (typeof window === 'undefined') return false

  try {
    return Boolean(window.sessionStorage.getItem(LEAD_FLAG_KEY))
  } catch {
    return false
  }
}

/**
 * The form that produced the pending lead, or null when unknown (a legacy '1'
 * flag, or a caller that passed no source).
 *
 * Must be read BEFORE `clearLeadSubmitted()` — clearing drops the source too.
 */
export function peekLeadSource(): string | null {
  if (typeof window === 'undefined') return null

  try {
    const value = window.sessionStorage.getItem(LEAD_FLAG_KEY)
    return value && value !== '1' ? value : null
  } catch {
    return null
  }
}

/** Short-lived hashes survive hard navigation when gtag was not ready at submit time. */
export function peekLeadUserData(): EnhancedConversionData | undefined {
  if (typeof window === 'undefined') return
  try {
    const raw = window.sessionStorage.getItem(LEAD_USER_DATA_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    const hash = /^[a-f0-9]{64}$/
    if (
      typeof parsed?.expiresAt === 'number' &&
      parsed.expiresAt > Date.now() &&
      parsed.expiresAt <= Date.now() + LEAD_USER_DATA_TTL_MS &&
      typeof parsed.data?.sha256_email_address === 'string' &&
      hash.test(parsed.data.sha256_email_address)
    ) {
      const data: EnhancedConversionData = {
        sha256_email_address: parsed.data.sha256_email_address,
      }
      if (
        typeof parsed.data.sha256_phone_number === 'string' &&
        hash.test(parsed.data.sha256_phone_number)
      ) {
        data.sha256_phone_number = parsed.data.sha256_phone_number
      }
      return data
    }
    window.sessionStorage.removeItem(LEAD_USER_DATA_KEY)
  } catch {
    // Corrupt/blocked storage must not break the thank-you page.
  }
}

export function clearLeadSubmitted(): void {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.removeItem(LEAD_USER_DATA_KEY)
    window.sessionStorage.removeItem(LEAD_FLAG_KEY)
  } catch {
    // Ignore removal errors.
  }
}
